'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Card, type BadgeVariant } from '@/components/motor-insurance/enterprise-navy';

const INSURANCE_SERVICE_URL =
  process.env.NEXT_PUBLIC_INSURANCE_SERVICE_URL || 'http://localhost:8004';

type DocumentState = 'missing' | 'pending' | 'approved' | 'rejected';

interface RequiredDocument {
  checklist_item_id: string;
  document_name: string;
  is_mandatory: boolean;
  max_size_mb: number;
  state: DocumentState;
}

interface RequiredDocumentsResponse {
  status_code: string;
  can_proceed: boolean;
  documents: RequiredDocument[];
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; field?: string };
}

// Why the list could not be loaded. `status` is the HTTP status of the refusal, or
// null when no usable answer arrived (network error, unreadable body).
interface LoadFailure {
  status: number | null;
}

// Statuses where the server told us something definite about the caller or the
// request. Those must not be reported as "you can still continue".
const DEFINITE_FAILURE_MESSAGE: Record<number, string> = {
  401: 'Your session has expired. Please sign in again.',
  403: "You do not have access to this request's documents.",
  404: 'This request was not found.',
};

const FAIL_OPEN_MESSAGE =
  'Could not load required documents. You can still continue; the system will tell you if a document is missing.';

function loadFailureMessage(failure: LoadFailure): string {
  return (failure.status !== null && DEFINITE_FAILURE_MESSAGE[failure.status]) || FAIL_OPEN_MESSAGE;
}

const STATE_CHIP: Record<DocumentState, { label: string; variant: BadgeVariant }> = {
  missing: { label: 'Missing', variant: 'danger' },
  pending: { label: 'Pending review', variant: 'info' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
};

// Mirrors the server's upload allowlist (app/core/upload_validation.py).
const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.mp4,.mov';

async function parseApiError(res: Response): Promise<string> {
  const body: ApiErrorBody = await res.json().catch(() => ({}));
  return body.error?.message || `Request failed (${res.status})`;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 0',
  borderTop: '1px solid var(--border, #D6DCE1)',
};

const uploadTriggerStyle = (disabled: boolean, filled: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '6px 14px',
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  background: filled ? 'var(--surface, #fff)' : 'var(--primary, #96650D)',
  color: filled ? 'var(--text, #1B2733)' : '#fff',
  border: filled ? '1px solid var(--border, #D6DCE1)' : 'none',
});

const warningStyle: React.CSSProperties = {
  background: '#FBEBD2',
  color: '#8A5A0B',
  padding: 10,
  borderRadius: 4,
  fontSize: 13,
  marginTop: 12,
};

const errorStyle: React.CSSProperties = {
  background: '#F8E1DE',
  color: '#9B2B22',
  padding: 10,
  borderRadius: 4,
  fontSize: 13,
  marginTop: 12,
};

const failureBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const retryButtonStyle: React.CSSProperties = {
  padding: '4px 12px',
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  background: 'var(--surface, #fff)',
  color: 'var(--text, #1B2733)',
  border: '1px solid var(--border, #D6DCE1)',
};

export interface RequiredDocumentsPanelProps {
  quoteRequestId: string;
  getToken: () => string | null;
  onChange?: (canProceed: boolean) => void;
}

export function RequiredDocumentsPanel({ quoteRequestId, getToken, onChange }: RequiredDocumentsPanelProps) {
  const [documents, setDocuments] = useState<RequiredDocument[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Checklist item ids with an upload in flight; each row is busy only for its own upload.
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(() => new Set());

  // Parents often pass fresh function instances on every render; keep the latest
  // ones in refs so reloading depends only on the quote request id.
  const getTokenRef = useRef(getToken);
  const onChangeRef = useRef(onChange);
  getTokenRef.current = getToken;
  onChangeRef.current = onChange;

  // Out-of-order protection: every load takes a number, and only the newest load for
  // the id currently on screen may write state. activeIdRef is null once unmounted.
  const loadSeqRef = useRef(0);
  const activeIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current || activeIdRef.current !== quoteRequestId;

    // The server stays the authority: if this panel cannot load, do not lock the
    // screen's actions; the server still refuses a move when documents are missing.
    const failLoad = (status: number | null) => {
      setDocuments([]);
      setLoadFailure({ status });
      onChangeRef.current?.(true);
    };

    try {
      const res = await fetch(
        `${INSURANCE_SERVICE_URL}/api/v1/quote-requests/${encodeURIComponent(quoteRequestId)}/required-documents`,
        { headers: { Authorization: `Bearer ${getTokenRef.current()}` } }
      );
      if (!res.ok) {
        if (isStale()) return;
        console.error('Failed to load required documents', { status: res.status });
        failLoad(res.status);
        return;
      }
      const data: RequiredDocumentsResponse = await res.json();
      if (isStale()) return;
      setDocuments(data.documents);
      setLoadFailure(null);
      onChangeRef.current?.(data.can_proceed);
    } catch (error) {
      if (isStale()) return;
      console.error('Failed to load required documents', error);
      failLoad(null);
    }
  }, [quoteRequestId]);

  useEffect(() => {
    activeIdRef.current = quoteRequestId;
    load();
    return () => {
      activeIdRef.current = null;
    };
  }, [load, quoteRequestId]);

  function setBusy(checklistItemId: string, busy: boolean) {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(checklistItemId);
      else next.delete(checklistItemId);
      return next;
    });
  }

  async function handleUpload(item: RequiredDocument, file: File) {
    setUploadError(null);
    setBusy(item.checklist_item_id, true);
    try {
      const form = new FormData();
      form.append('checklist_item_id', item.checklist_item_id);
      form.append('file', file);
      // No Content-Type header: the browser sets the multipart boundary itself.
      const res = await fetch(
        `${INSURANCE_SERVICE_URL}/api/v1/quote-requests/${encodeURIComponent(quoteRequestId)}/documents`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${getTokenRef.current()}` },
          body: form,
        }
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      await load();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Failed to upload document.');
    } finally {
      setBusy(item.checklist_item_id, false);
    }
  }

  return (
    <Card style={{ padding: 16, marginBottom: 16 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Required documents</h2>
      <p style={{ fontSize: 12, color: 'var(--text-dim, #5B6B7A)', margin: '4px 0 8px' }}>
        Documents needed before this request can move on from its current status.
      </p>

      {documents === null && <p style={{ fontSize: 13 }}>Loading&hellip;</p>}

      {loadFailure && (
        <div
          role="alert"
          style={{
            ...(loadFailure.status !== null && loadFailure.status in DEFINITE_FAILURE_MESSAGE ? errorStyle : warningStyle),
            ...failureBoxStyle,
          }}
        >
          <span>{loadFailureMessage(loadFailure)}</span>
          <button type="button" style={retryButtonStyle} onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {documents !== null && !loadFailure && documents.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-dim, #5B6B7A)' }}>No documents required at this status.</p>
      )}

      {documents !== null &&
        documents.map((item) => {
          const chip = STATE_CHIP[item.state];
          const isBusy = busyIds.has(item.checklist_item_id);
          const needsFile = item.state === 'missing' || item.state === 'rejected';
          return (
            <div key={item.checklist_item_id} style={rowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14 }}>{item.document_name}</span>
                {!item.is_mandatory && (
                  <span style={{ fontSize: 11, color: 'var(--text-dim, #5B6B7A)' }}>Optional</span>
                )}
                <Badge variant={chip.variant}>{chip.label}</Badge>
              </div>
              <label style={uploadTriggerStyle(isBusy, !needsFile)}>
                {isBusy ? 'Uploading…' : needsFile ? 'Upload' : 'Replace'}
                <input
                  type="file"
                  aria-label={`Upload ${item.document_name}`}
                  accept={ACCEPTED_FILE_TYPES}
                  disabled={isBusy}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) handleUpload(item, file);
                  }}
                />
              </label>
            </div>
          );
        })}

      {uploadError && (
        <div role="alert" style={errorStyle}>
          {uploadError}
        </div>
      )}
    </Card>
  );
}

export default RequiredDocumentsPanel;
